import moment from 'moment';
import { Dispatch, SetStateAction, useRef, useState } from 'react';
import { useId } from '@fluentui/react-hooks';
import { DelayedRender } from '@fluentui/react';
import {
  PopupForm,
  PopupHeading,
  Containter,
  ButtonContainer,
  DiscardButton,
  SaveButton,
  PopupButton,
  PopupCallout,
  ButtonAddIcon,
  ButtonDismissIcon,
  Alert,
  AlertIcon,
  InputContainer,
  PopupInput,
  PopupLabel,
  PopupTextarea,
  InfoIconHours,
  CheckIconHours,
  Exclamation,
} from './styles/TimesheetsDayEntryPopup.styled';
import { useCreateDayEntry } from './hooks/useCreateDayEntry';
import { useUpdateDayEntry } from './hooks/useUpdateDayEntry';
import {
  DESCRIPTION_REQUIRED_VALIDATION_MESSAGE,
  HOURS_REQUIRED_VALIDATION_MESSAGE,
} from 'src/tabs/constants/Timesheets.constants';
import { DEFAULT_DATE_FORMAT } from 'src/tabs/constants/Common.constants';

export interface Form {
  hours: number;
  description: string;
}

interface TimeSheetsDayEntryPopupProps {
  date: string;
  hours: number;
  description: string;
  assignmentId: string;
  timeTrackingId: string;
  forecastSupply?: number;
  setShowSuccessOverlay: Dispatch<SetStateAction<boolean>>;
  isCalloutVisible: boolean;
  toggleOnIsCalloutVisible: () => void;
  isDisabled?: boolean;
  statecode: number;
  status?: number;
  getAssignments: () => void;
  setAnyPopupsOpen?: (value: boolean) => void;
  setIsCalloutVisible: (value: boolean) => void;
}

export const TimesheetsDayEntryPopup = ({
  hours,
  date,
  description,
  assignmentId,
  timeTrackingId,
  forecastSupply,
  setShowSuccessOverlay,
  isCalloutVisible,
  toggleOnIsCalloutVisible,
  setIsCalloutVisible,
  isDisabled,
  status,
  getAssignments,
  setAnyPopupsOpen,
}: TimeSheetsDayEntryPopupProps) => {
  const [exclamationPosition, setExclamationPosition] = useState(false);
  const alertHoursRef = useRef(null);
  const checkHoursRef = useRef(null);
  const hoursRef = useRef(null);
  const descriptionRef = useRef(null);
  const errorRef = useRef(null);
  const { createDayEntry } = useCreateDayEntry(
    moment(date).format(DEFAULT_DATE_FORMAT)
  );
  const { updateDayEntry } = useUpdateDayEntry();
  const calloutButtonId = useId('callout-button');
  const formattedDate = moment(new Date(date)).format('D MMMM YYYY');

  const submitHandler = async () => {
    const hoursValue = hoursRef?.current?.value;
    const descriptionValue = descriptionRef?.current?.value.trim();

    if (!timeTrackingId) {
      if (!hoursValue) {
        errorRef.current.style.display = 'flex';
        errorRef.current.children[2].innerText =
          'Hours must be between 1 and 24.';
        alertHoursRef.current.style.display = 'block';
        checkHoursRef.current.style.display = 'none';
      } else if (hoursValue > 0 && !descriptionValue) {
        errorRef.current.style.display = 'flex';
        errorRef.current.children[2].innerText =
          DESCRIPTION_REQUIRED_VALIDATION_MESSAGE;
      }
      if (
        (!hoursValue && !descriptionValue) ||
        (hoursValue == 0 && descriptionValue) ||
        !hoursValue
      ) {
        errorRef.current.style.display = 'flex';
        errorRef.current.children[2].innerText =
          HOURS_REQUIRED_VALIDATION_MESSAGE;
        alertHoursRef.current.style.display = 'block';
        checkHoursRef.current.style.display = 'none';
        return;
      } else if (
        hoursValue > 24 ||
        hoursValue < 0 ||
        !Number.isInteger(+hoursValue)
      ) {
        if (hoursValue > 24) {
          errorRef.current.style.display = 'flex';
          errorRef.current.children[2].innerText =
            'Value must be less than or equal to 24.';
          alertHoursRef.current.style.display = 'block';
          checkHoursRef.current.style.display = 'none';
          return;
        } else if (hoursValue < 0) {
          errorRef.current.style.display = 'flex';
          errorRef.current.children[2].innerText =
            'Value must be greater than or equal to 0.';
          alertHoursRef.current.style.display = 'block';
          checkHoursRef.current.style.display = 'none';
          return;
        } else if (!Number.isInteger(+hoursValue)) {
          errorRef.current.style.display = 'flex';
          errorRef.current.children[2].innerText = `Please enter a valid value. The two nearest valid values are ${Math.floor(
            +hoursValue
          )} and ${Math.ceil(+hoursValue)}.`;
          alertHoursRef.current.style.display = 'block';
          checkHoursRef.current.style.display = 'none';
          setExclamationPosition(true);
          return;
        }
      } else if (!descriptionValue) {
        errorRef.current.style.display = 'flex';
        errorRef.current.children[2].innerText =
          DESCRIPTION_REQUIRED_VALIDATION_MESSAGE;
        return;
      }
    } else {
      if (!hoursValue) {
        errorRef.current.style.display = 'flex';
        errorRef.current.children[2].innerText =
          HOURS_REQUIRED_VALIDATION_MESSAGE;
        alertHoursRef.current.style.display = 'block';
        checkHoursRef.current.style.display = 'none';
        return;
      } else if (
        hoursValue > 24 ||
        hoursValue < 0 ||
        !Number.isInteger(+hoursValue)
      ) {
        if (hoursValue > 24) {
          errorRef.current.style.display = 'flex';
          errorRef.current.children[2].innerText =
            'Value must be less than or equal to 24.';
          alertHoursRef.current.style.display = 'block';
          checkHoursRef.current.style.display = 'none';
          return;
        } else if (hoursValue < 0) {
          errorRef.current.style.display = 'flex';
          errorRef.current.children[2].innerText =
            'Value must be greater than or equal to 0.';
          alertHoursRef.current.style.display = 'block';
          checkHoursRef.current.style.display = 'none';
          return;
        } else if (!Number.isInteger(+hoursValue)) {
          errorRef.current.style.display = 'flex';
          errorRef.current.children[2].innerText = `Please enter a valid value. The two nearest valid values are ${Math.floor(
            +hoursValue
          )} and ${Math.ceil(+hoursValue)}.`;
          alertHoursRef.current.style.display = 'block';
          checkHoursRef.current.style.display = 'none';
          setExclamationPosition(true);
          return;
        }
      } else if (hoursValue > 0 && !descriptionValue) {
        errorRef.current.style.display = 'flex';
        errorRef.current.children[2].innerText =
          DESCRIPTION_REQUIRED_VALIDATION_MESSAGE;
        return;
      }
    }

    const onSuccess = () => {
      setShowSuccessOverlay(true);
      setTimeout(() => {
        setShowSuccessOverlay(false);
      }, 2000);
    };

    if (
      (hours ||
        description ||
        timeTrackingId ||
        forecastSupply !== undefined) &&
      status === 1
    ) {
      toggleOnIsCalloutVisible();
      await updateDayEntry(
        { hours: +hoursValue, description: descriptionValue },
        timeTrackingId,
        assignmentId
      );
      getAssignments();
      onSuccess();
    } else {
      toggleOnIsCalloutVisible();
      await createDayEntry(
        { hours: +hoursValue, description: descriptionValue },
        assignmentId
      );
      getAssignments();
      onSuccess();
    }
  };

  const handleHoursInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (+e.target.value > 0 && +e.target.value <= 24) {
      errorRef.current.style.display = 'none';
      alertHoursRef.current.style.display = 'none';
      checkHoursRef.current.style.display = 'block';
      setExclamationPosition(false);
    }
  };

  const handleDiscard = () => {
    setIsCalloutVisible(false);
    setAnyPopupsOpen(false);
  };

  return (
    <>
      <PopupButton id={calloutButtonId} onClick={toggleOnIsCalloutVisible}>
        {isCalloutVisible ? <ButtonDismissIcon /> : <ButtonAddIcon />}
      </PopupButton>
      {isCalloutVisible && (
        <PopupCallout
          target={`#${calloutButtonId}`}
          focusTrapProps={{
            isClickableOutsideFocusTrap: true,
            disableFirstFocus: true,
          }}
          role="alert"
        >
          <DelayedRender>
            <PopupForm>
              <PopupHeading>{formattedDate}</PopupHeading>
              <Containter>
                <InputContainer>
                  <PopupLabel htmlFor="input">Your time in hours*</PopupLabel>
                  <PopupInput
                    id="input"
                    type="number"
                    min={0}
                    max={24}
                    step={1}
                    placeholder="Hours"
                    defaultValue={hours}
                    pattern="\d*"
                    tabIndex={0}
                    ref={hoursRef}
                    disabled={isDisabled}
                    required
                    onChange={(e) => handleHoursInput(e)}
                  />
                  <InfoIconHours ref={alertHoursRef} />
                  <CheckIconHours ref={checkHoursRef} />
                </InputContainer>
                <ButtonContainer>
                  <DiscardButton onClick={handleDiscard}>Discard</DiscardButton>
                  {!isDisabled && (
                    <SaveButton onClick={() => submitHandler()}>
                      Save
                    </SaveButton>
                  )}
                </ButtonContainer>
              </Containter>
              <>
                <PopupLabel htmlFor="description">Description*</PopupLabel>
                <PopupTextarea
                  id="description"
                  placeholder="Your notes..."
                  tabIndex={0}
                  ref={descriptionRef}
                  defaultValue={description}
                  disabled={isDisabled}
                  onChange={(e) => {
                    if (e.target.value.trim()) {
                      errorRef.current.style.display = 'none';
                    }
                  }}
                />
              </>
              <Alert ref={errorRef}>
                <AlertIcon />
                <Exclamation exclamationPosition={exclamationPosition}>
                  !
                </Exclamation>{' '}
                <p></p>
              </Alert>
            </PopupForm>
          </DelayedRender>
        </PopupCallout>
      )}
    </>
  );
};
